import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../shared/api/client";
import type {
  PageResponse,
  ReleaseDetail,
  ReleaseScopeChangeReason,
  ReleaseScopeItem,
  WorkItemSummary,
} from "../../shared/types/api";
import {
  Button,
  Drawer,
  Field,
  InlineAlert,
  Input,
  Select,
  StatusBadge,
  Textarea,
} from "../../shared/ui";
import { ConflictNotice } from "./ConflictNotice";
import { useReleaseMutation } from "./useReleaseMutation";
import {
  REASON_REQUIRING_COMMENT,
  RELATION_LABELS,
  SCOPE_CHANGE_REASONS,
} from "./releaseVocabulary";

/**
 * How many matches are offered.
 *
 * <p>Ten was too few to be honest about it: a project where eleven items match shows ten and
 * says nothing about the eleventh, and the one left out is whichever is oldest — often the
 * exact key somebody typed.
 */
const LIMIT = 25;

const CHILD_TYPES = ["USER_STORY", "TECHNICAL_TASK", "BUG", "SUBTASK"] as const;
const CHILD_TYPE_LABELS: Record<string, string> = {
  USER_STORY: "Stories",
  TECHNICAL_TASK: "Tasks",
  BUG: "Bugs",
  SUBTASK: "Subtasks",
};

/**
 * Putting work into a release (§10, §12, §13).
 *
 * <p>Two modes, because §12 asks for them to behave differently. A single item is added directly.
 * An epic is not: its contents are listed with a count and a set of checkboxes, and only what
 * somebody ticks becomes a mapping. Sweeping the children in implicitly would mean the release's
 * scope changed every time a story was added to that epic three weeks later, without anybody
 * deciding it should.
 *
 * <p>After the scope is frozen, the reason field appears. It is not decoration: the server refuses
 * the write without it, because the organisation was told the scope was settled.
 */
export function AddScopeDrawer({
  release,
  open,
  onClose,
  onSaved,
}: {
  release: ReleaseDetail;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [query, setQuery] = useState("");
  const [chosen, setChosen] = useState<WorkItemSummary | null>(null);
  const [relationType, setRelationType] = useState("PRIMARY_TARGET");
  const [types, setTypes] = useState<string[]>([...CHILD_TYPES]);
  const [includeEpic, setIncludeEpic] = useState(false);
  const [reason, setReason] = useState<ReleaseScopeChangeReason | "">("");
  const [comment, setComment] = useState("");
  const { run, error, conflict, saving, reset } = useReleaseMutation(release.id);

  const frozen = release.scopeFrozenAt != null;

  useEffect(() => {
    if (open) {
      setQuery("");
      setChosen(null);
      setRelationType("PRIMARY_TARGET");
      setTypes([...CHILD_TYPES]);
      setIncludeEpic(false);
      setReason("");
      setComment("");
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const results = useQuery({
    queryKey: ["release-scope-search", release.projectId, query],
    queryFn: async () => {
      /*
       * The parameter is "q". It was "query", which the endpoint does not take — Spring ignored
       * it and answered with the first items of the project, newest first, whatever was typed.
       * The box looked like it worked: it returned rows, they just never matched, and
       * everything older than the ten most recent items was unreachable.
       */
      const params = new URLSearchParams({ projectId: release.projectId, size: String(LIMIT) });
      params.set("q", query.trim());
      const page = await api.get<PageResponse<WorkItemSummary>>(`/api/v1/work-items?${params}`);

      /*
       * The item whose key was typed exactly goes first.
       *
       * <p>The server answers newest first, which buries the one thing somebody typing a whole
       * key is certainly looking for: searching "GE-3" matched eleven items, and GE-3 itself —
       * the oldest of them — came last, past the end of the page. Everything else keeps the
       * order it arrived in, so this only ever promotes, never reshuffles.
       */
      const typed = query.trim().toLowerCase();
      const content = [...page.content].sort((a, b) =>
        Number(b.itemKey.toLowerCase() === typed) - Number(a.itemKey.toLowerCase() === typed));
      return { ...page, content };
    },
    enabled: open && query.trim().length >= 2,
  });

  /* §12: what an epic holds, fetched only once one is chosen, and only to be shown. */
  const epicContents = useQuery({
    queryKey: ["release-epic-contents", release.id, chosen?.id],
    queryFn: () =>
      api.get<ReleaseScopeItem[]>(
        `/api/v1/releases/${release.id}/scope/epic-contents?epicId=${chosen!.id}`,
      ),
    enabled: open && chosen?.workItemType === "EPIC",
  });

  const isEpic = chosen?.workItemType === "EPIC";
  const needsComment = reason !== "" && REASON_REQUIRING_COMMENT.includes(reason);
  const canSubmit =
    chosen != null
    && (!frozen || (reason !== "" && (!needsComment || comment.trim() !== "")))
    && (!isEpic || types.length > 0 || includeEpic);

  const submit = () =>
    void run(
      () =>
        isEpic
          ? api.post(`/api/v1/releases/${release.id}/scope/epic`, {
              epicId: chosen!.id,
              workItemTypes: types,
              includeEpic,
              reason: reason === "" ? null : reason,
              comment: comment.trim() === "" ? null : comment.trim(),
            })
          : api.post(`/api/v1/releases/${release.id}/scope/items`, {
              workItemId: chosen!.id,
              relationType,
              reason: reason === "" ? null : reason,
              comment: comment.trim() === "" ? null : comment.trim(),
            }),
      () => {
        onSaved();
        onClose();
      },
    );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Add work to this release"
      subtitle={`${release.releaseKey} — ${release.releaseName}`}
      footer={
        <div className="flex justify-between gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canSubmit} loading={saving}>
            {saving ? "Adding…" : "Add to release"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {conflict ? <ConflictNotice onRefresh={onSaved} /> : error && (
          <InlineAlert tone="error">{error}</InlineAlert>
        )}

        {frozen && (
          <InlineAlert tone="warning">
            This release's scope is frozen. Anything added now is recorded as a change to a settled
            scope, with the reason you give below.
          </InlineAlert>
        )}

        {chosen ? (
          <div className="flex items-start justify-between gap-3 rounded-lg border border-brand-200 bg-brand-50 px-3 py-2">
            <div className="min-w-0">
              <p className="font-mono text-xs text-brand-700">{chosen.itemKey}</p>
              <p className="truncate text-sm text-ink-900">{chosen.title}</p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setChosen(null)}>
              Change
            </Button>
          </div>
        ) : (
          <Field label="Find the work" hint="Search by key or title within this project.">
            <Input
              autoFocus
              value={query}
              placeholder="PRB-14, payment…"
              onChange={(event) => setQuery(event.target.value)}
            />
            {query.trim().length >= 2 && (
              <div className="mt-1.5 max-h-56 overflow-y-auto rounded-lg border border-ink-200">
                {results.isPending && (
                  <p className="px-2.5 py-2 text-xs text-ink-500">Looking…</p>
                )}
                {results.data?.content.length === 0 && (
                  <p className="px-2.5 py-2 text-xs text-ink-500">Nothing matches.</p>
                )}
                {/* A truncated list that says nothing reads as "that is all there is". */}
                {(results.data?.totalElements ?? 0) > LIMIT && (
                  <p className="border-b border-ink-100 px-2.5 py-2 text-xs text-ink-500">
                    Showing {LIMIT} of {results.data?.totalElements}. Type more to narrow it.
                  </p>
                )}
                {results.data?.content.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setChosen(item)}
                    className="flex w-full items-baseline gap-2 px-2.5 py-2 text-left text-sm hover:bg-ink-50"
                  >
                    <span className="font-mono text-xs text-ink-500">{item.itemKey}</span>
                    <span className="truncate text-ink-800">{item.title}</span>
                  </button>
                ))}
              </div>
            )}
          </Field>
        )}

        {isEpic && (
          <div className="rounded-lg border border-ink-200 px-3 py-2.5">
            <p className="text-sm font-medium text-ink-900">
              This epic contains {epicContents.data?.length ?? 0} work items.
            </p>
            <p className="mt-0.5 text-xs text-ink-600">
              Choose what to bring in. Each becomes its own entry, so the scope stays auditable —
              work added to this epic later will not join by itself.
            </p>
            <div className="mt-2 space-y-1.5">
              {CHILD_TYPES.map((type) => {
                const count =
                  epicContents.data?.filter((item) => item.workItemType === type).length ?? 0;
                return (
                  <label key={type} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={types.includes(type)}
                      disabled={count === 0}
                      onChange={(event) =>
                        setTypes((current) =>
                          event.target.checked
                            ? [...current, type]
                            : current.filter((entry) => entry !== type),
                        )
                      }
                    />
                    <span className={count === 0 ? "text-ink-400" : "text-ink-800"}>
                      {CHILD_TYPE_LABELS[type]}
                    </span>
                    <StatusBadge tone="gray">{count}</StatusBadge>
                  </label>
                );
              })}
              <label className="flex items-center gap-2 border-t border-ink-100 pt-2 text-sm">
                <input
                  type="checkbox"
                  checked={includeEpic}
                  onChange={(event) => setIncludeEpic(event.target.checked)}
                />
                <span className="text-ink-800">The epic itself</span>
              </label>
            </div>
          </div>
        )}

        {chosen && !isEpic && (
          <Field label="How it relates" hint="An item may be the target of one release and also ship in another.">
            <Select
              value={relationType}
              onChange={(event) => setRelationType(event.target.value)}
            >
              {Object.entries(RELATION_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          </Field>
        )}

        {frozen && (
          <>
            <Field label="Reason" required>
              <Select
                value={reason}
                onChange={(event) =>
                  setReason(event.target.value as ReleaseScopeChangeReason | "")
                }
              >
                <option value="">Choose a reason…</option>
                {SCOPE_CHANGE_REASONS.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            </Field>
            <Field
              label="What happened"
              required={needsComment}
              hint={needsComment ? "\"Something else\" needs saying what it was." : "Optional."}
            >
              <Textarea
                rows={2}
                value={comment}
                onChange={(event) => setComment(event.target.value)}
              />
            </Field>
          </>
        )}
      </div>
    </Drawer>
  );
}
